import React, { useState } from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import {
  Alert,
  Descriptions,
  Tooltip,
  Typography,
  Form,
  Modal,
  Table,
  Col,
  Row,
  Button,
  Space,
  message,
} from 'antd';

import {
  GetParticipantPracticeDiffQuery,
  GetParticipantPracticeDiffDocument,
  ListParticipantUploadQuery,
  NetworkDetailsFragment,
  useApplyParticipantPracticeDiffMutation,
  useListAlignmentUploadsQuery,
  useDeleteAlignmentReportByFileIdMutation,
  useListParticipantUploadQuery,
  useDeleteParticipantListByFileIdMutation,
  useListProviderAlignmentUploadsQuery,
  useDeleteProviderAlignmentReportByFileIdMutation,
  useListPvaResponseUploadQuery,
  useDeletePvaResponseByFileIdMutation,
  useListHedrTemplateUploadsQuery,
  useDeleteHedrTemplateByFileIdMutation,
  useListHealthEquityReportUploadsQuery,
  useDeleteHealthEquityReportsByFileIdMutation,
  useUpdateNetworkMutation,
} from '../../graphql/generated';
import PracticeTag from '../practices/PracticeTag';
import OrganizationTag from '../organizations/OrganizationTag';
import SwitchRoleButton from '../utils/SwitchRoleButton';
import KeyValueForm from '../utils/KeyValueForm';
import {
  DeleteOutlined,
  FileOutlined,
  FileSyncOutlined,
  DiffOutlined,
  MinusCircleFilled,
  PlusCircleFilled,
  QuestionCircleOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { StethoscopeOutlined, UserInjuredOutlined } from '../MabelIcons';
import CMSUploadModal from '../utils/CMSUploadModal';
import SignedAWSURL from '../utils/SignedAWSURL';
import NetworkApprovedTemplates from './NetworkApprovedTemplates';
import {
  computeParticipantPracticeDiff,
  ParticipantDiffNetwork,
} from './participantPracticeDiff';

const NetworkDescription: React.FC<{
  network: NetworkDetailsFragment;
  clientOrganizationId?: string;
}> = ({ network, clientOrganizationId }) => {
  const primaryOrganizationNetwork = network.organization_networks.find(
    (on) => !on.is_archived && on.is_primary_org_for_network
  );

  const [
    updateNetworkMutation,
    updateNetworkMutationResult,
  ] = useUpdateNetworkMutation();

  const [mergeVarModalVisible, setMergeVarModalVisible] = useState(false);
  const [mergeVarForm] = Form.useForm();
  return (
    <Descriptions
      title={network.network_name}
      layout="horizontal"
      column={1}
      style={{ margin: '24px 48px' }}
    >
      <Descriptions.Item label="ID">{network.network_id}</Descriptions.Item>
      <Descriptions.Item label="Network Number">
        {network.network_number}
      </Descriptions.Item>
      <Descriptions.Item label="ACO ID">{network.dc_id}</Descriptions.Item>
      <Descriptions.Item label="Slug">{network.network_slug}</Descriptions.Item>
      <Descriptions.Item label="Description">
        {network.network_description}
      </Descriptions.Item>
      <Descriptions.Item label="Default Practice">
        {network.default_practice && (
          <PracticeTag practice={network.default_practice} />
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Primary Org">
        {primaryOrganizationNetwork && (
          <OrganizationTag
            organization={primaryOrganizationNetwork.organization}
          />
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Broker Phone (emails)">
        {network.broker_phone_for_emails}
      </Descriptions.Item>
      <Descriptions.Item label="Broker Phone (letters)">
        {network.broker_phone_for_letters}
      </Descriptions.Item>
      <Descriptions.Item label="Broker URL (emails)">
        {network.broker_url_for_emails}
      </Descriptions.Item>
      <Descriptions.Item label="Broker URL (letters)">
        {network.broker_url_for_letters}
      </Descriptions.Item>
      <Descriptions.Item label="Broker Disclaimer">
        {network.broker_disclaimer}
      </Descriptions.Item>
      <Descriptions.Item label="Logo" style={{ display: 'flex' }}>
        {network.network_logo ? (
          <img
            src={network.network_logo}
            style={{ width: 320, height: 120 }}
            alt="network logo"
          />
        ) : null}
      </Descriptions.Item>
      <Descriptions.Item label="Color" style={{ display: 'flex' }}>
        {network.network_color ? (
          <div
            style={{
              padding: 4,
              margin: 2,
              background: '#fff',
              borderRadius: '2px',
              boxShadow: '0 0 0 1px rgba(0,0,0,.1)',
              display: 'inline-block',
            }}
          >
            <div
              style={{
                width: '20px',
                height: '20px',
                borderRadius: '2px',
                background: network.network_color,
              }}
            />
          </div>
        ) : null}
      </Descriptions.Item>
      <Descriptions.Item label="Network Variables">
        <Tooltip
          title={
            <pre>
              {JSON.stringify(network.network_merge_vars || {}, null, 2)}
            </pre>
          }
        >
          <Typography.Text
            ellipsis
            code
            editable={{
              tooltip: false,
              editing: false,
              onStart: () => {
                setMergeVarModalVisible(true);
              },
            }}
          >
            {JSON.stringify(network.network_merge_vars || {}, null, 2)}
          </Typography.Text>
        </Tooltip>
        <Modal
          visible={mergeVarModalVisible}
          title={
            <Typography.Text>
              Modify Network Variables{' '}
              <Tooltip title="Values will only be used if not defined at the practice, campaign, component, or patient level">
                <QuestionCircleOutlined />
              </Tooltip>
            </Typography.Text>
          }
          okText="Save"
          cancelText="Cancel"
          confirmLoading={updateNetworkMutationResult.loading}
          onCancel={() => {
            mergeVarForm.resetFields();
            setMergeVarModalVisible(false);
          }}
          onOk={mergeVarForm.submit}
        >
          <KeyValueForm
            form={mergeVarForm}
            initialValues={network.network_merge_vars}
            onFinish={(vars) => {
              updateNetworkMutation({
                variables: {
                  network_id: network.network_id,
                  changes: { network_merge_vars: vars },
                },
              }).then(() => setMergeVarModalVisible(false));
            }}
          />
        </Modal>
      </Descriptions.Item>
      <Descriptions.Item label="4i Files Loaded">
        {network.dc_id && (
          <CMSTables
            network_id={network.network_id}
            dc_id={network.dc_id}
            network_number={network.network_number}
            clientOrganizationId={clientOrganizationId}
          />
        )}
      </Descriptions.Item>
      <Descriptions.Item label="Approved Templates">
        <NetworkApprovedTemplates network_id={network.network_id} />
      </Descriptions.Item>
      <Descriptions.Item>
        <SwitchRoleButton
          network_id={network.network_id}
          changeRole="network_user"
        />
      </Descriptions.Item>
    </Descriptions>
  );
};

export const CMSTables: React.FC<{
  network_id: string;
  dc_id: string;
  network_number: number;
  showDeleteActions?: boolean;
  includeHedrTables?: boolean;
  clientOrganizationId?: string;
}> = ({
  network_id,
  dc_id,
  network_number,
  showDeleteActions = true,
  includeHedrTables = true,
  clientOrganizationId,
}) => {
  type ParticipantUploadRow = ListParticipantUploadQuery['cms_dce_participants'][number];
  type ParticipantDiffPractice = {
    practice_id?: string;
    practice_name: string;
    legal_business_name?: string | null;
    tin?: string | null;
    ccn?: string | null;
    primary_network_id: string;
    network_name: string;
  };
  type ParticipantDiffPreview = {
    file_upload_id: string;
    file_name: string;
    archives: ParticipantDiffPractice[];
    reinstates: ParticipantDiffPractice[];
    creates: ParticipantDiffPractice[];
    archive_conflicts: ParticipantDiffPractice[];
    reinstate_conflicts: ParticipantDiffPractice[];
    skipped_no_tin: number;
  };

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [participantDiffPreview, setParticipantDiffPreview] = useState<
    ParticipantDiffPreview | undefined
  >();
  const [participantDiffLoadingId, setParticipantDiffLoadingId] = useState<
    string | undefined
  >();
  const [participantDiffApplying, setParticipantDiffApplying] = useState(false);
  const [fileCategory, setfileCategory] = useState<
    | 'hedr_submission_templates'
    | 'cms_alignment_reports'
    | 'cms_provider_alignment_reports'
    | 'cms_dce_participants'
    | 'cms_pva_responses'
    | 'health_equity_reports'
    | undefined
  >(undefined);

  const {
    data: pvaResponseData,
    loading: pvaResponseDataLoading,
    refetch: pvaResponseRefetch,
  } = useListPvaResponseUploadQuery({
    variables: { DC_ID: dc_id },
    notifyOnNetworkStatusChange: true,
  });
  const [
    deletePvaResponse,
    { loading: deletePVAResponseDataLoading },
  ] = useDeletePvaResponseByFileIdMutation();

  const {
    data: participantResponseData,
    loading: participantResponseDataLoading,
    refetch: participantResponseRefetch,
  } = useListParticipantUploadQuery({
    variables: { DC_ID: dc_id },
    notifyOnNetworkStatusChange: true,
  });
  const [
    deleteParticipantList,
    { loading: deleteParticipantListLoading },
  ] = useDeleteParticipantListByFileIdMutation();

  const apolloClient = useApolloClient();
  const [
    applyParticipantPracticeDiff,
  ] = useApplyParticipantPracticeDiffMutation();

  const buildParticipantDiffPreview = (
    file_upload_id: string,
    file_name: string,
    data: GetParticipantPracticeDiffQuery
  ): ParticipantDiffPreview => {
    if (!data.cms_dce_participants.length) {
      throw new Error('This upload has no Participant Provider rows');
    }

    const networks = data.networks.map((network) => ({
      ...network,
      network_number: network.network_number!,
      practices: network.practices,
    })) as ParticipantDiffNetwork[];
    const diff = computeParticipantPracticeDiff(
      data.cms_dce_participants,
      networks
    );
    if (diff.error) throw new Error(diff.error);

    const practicesById = new Map<string, ParticipantDiffPractice>();
    networks.forEach((network) => {
      network.practices.forEach((practice) => {
        practicesById.set(practice.practice_id, {
          ...practice,
          primary_network_id: practice.primary_network_id || network.network_id,
          network_name: network.network_name,
        });
      });
    });
    const networkNames = new Map(
      networks.map((network) => [network.network_id, network.network_name])
    );
    const openPracticeIds = new Set(
      data.data_change_requests
        .map(
          (request) =>
            (request.target_key as { practice_id?: string } | null | undefined)
              ?.practice_id
        )
        .filter((practiceId): practiceId is string => Boolean(practiceId))
    );
    const archiveConflictIds = new Set(
      diff.archives.filter((practiceId) => openPracticeIds.has(practiceId))
    );
    const reinstateConflictIds = new Set(
      diff.reinstates.filter((practiceId) => openPracticeIds.has(practiceId))
    );

    return {
      file_upload_id,
      file_name,
      archives: diff.archives
        .filter((practiceId) => !archiveConflictIds.has(practiceId))
        .map((practiceId) => practicesById.get(practiceId)!),
      reinstates: diff.reinstates
        .filter((practiceId) => !reinstateConflictIds.has(practiceId))
        .map((practiceId) => practicesById.get(practiceId)!),
      creates: diff.creates.map((practice) => ({
        ...practice,
        network_name: networkNames.get(practice.primary_network_id)!,
      })),
      archive_conflicts: Array.from(archiveConflictIds).map(
        (practiceId) => practicesById.get(practiceId)!
      ),
      reinstate_conflicts: Array.from(reinstateConflictIds).map(
        (practiceId) => practicesById.get(practiceId)!
      ),
      skipped_no_tin: diff.skipped_no_tin,
    };
  };

  const fetchParticipantDiffPreview = async (
    file_upload_id: string,
    file_name: string
  ) => {
    if (!clientOrganizationId) {
      throw new Error('Your account is not attached to a client organization');
    }
    const result = (await apolloClient.query({
      query: GetParticipantPracticeDiffDocument,
      variables: {
        file_upload_id,
        dc_id,
        organization_id: clientOrganizationId,
      },
      fetchPolicy: 'network-only',
    })) as { data?: GetParticipantPracticeDiffQuery };
    if (!result.data) throw new Error('Unable to load participant list data');
    return buildParticipantDiffPreview(file_upload_id, file_name, result.data);
  };

  const previewParticipantDiff = async (
    file_upload_id: string,
    file_name: string
  ) => {
    setParticipantDiffLoadingId(file_upload_id);
    try {
      setParticipantDiffPreview(
        await fetchParticipantDiffPreview(file_upload_id, file_name)
      );
    } catch (error) {
      message.error(
        (error as Error).message || 'Unable to compute participant list changes'
      );
    } finally {
      setParticipantDiffLoadingId(undefined);
    }
  };

  const comparableParticipantDiff = (preview: ParticipantDiffPreview) =>
    JSON.stringify({
      archives: preview.archives.map((practice) => practice.practice_id),
      reinstates: preview.reinstates.map((practice) => practice.practice_id),
      creates: preview.creates,
      archive_conflicts: preview.archive_conflicts.map(
        (practice) => practice.practice_id
      ),
      reinstate_conflicts: preview.reinstate_conflicts.map(
        (practice) => practice.practice_id
      ),
    });

  const applyParticipantDiff = async () => {
    if (!participantDiffPreview || !clientOrganizationId) return;
    setParticipantDiffApplying(true);
    try {
      const currentPreview = await fetchParticipantDiffPreview(
        participantDiffPreview.file_upload_id,
        participantDiffPreview.file_name
      );
      if (
        comparableParticipantDiff(currentPreview) !==
        comparableParticipantDiff(participantDiffPreview)
      ) {
        setParticipantDiffPreview(currentPreview);
        message.warning(
          'The proposed changes changed while this modal was open. Review them again before confirming.'
        );
        return;
      }
      if (
        currentPreview.archive_conflicts.length ||
        currentPreview.reinstate_conflicts.length
      ) {
        throw new Error(
          'Resolve the existing open change requests before applying this diff'
        );
      }

      const result = await applyParticipantPracticeDiff({
        variables: {
          practices: currentPreview.creates.map(
            ({ network_name: _networkName, ...practice }) => practice
          ),
          requests: [
            ...currentPreview.archives.map((practice) => ({
              target_table: 'practices',
              change_type: 'update',
              target_key: { practice_id: practice.practice_id },
              proposed_changes: { is_archived: true },
              original_values: { is_archived: false },
              client_organization_id: clientOrganizationId,
              batch_id: currentPreview.file_upload_id,
              submission_note:
                'Participant list diff from ' + currentPreview.file_name,
            })),
            ...currentPreview.reinstates.map((practice) => ({
              target_table: 'practices',
              change_type: 'update',
              target_key: { practice_id: practice.practice_id },
              proposed_changes: { is_archived: false },
              original_values: { is_archived: true },
              client_organization_id: clientOrganizationId,
              batch_id: currentPreview.file_upload_id,
              submission_note:
                'Participant list diff from ' + currentPreview.file_name,
            })),
          ],
        },
      });
      const created = result.data?.insert_practices?.affected_rows || 0;
      message.success(
        created.toLocaleString() +
          ' practices created, ' +
          currentPreview.archives.length.toLocaleString() +
          ' archive requests submitted, and ' +
          currentPreview.reinstates.length.toLocaleString() +
          ' unarchive requests submitted'
      );
      setParticipantDiffPreview(undefined);
    } catch (error) {
      message.error(
        (error as Error).message || 'Unable to apply participant list changes'
      );
    } finally {
      setParticipantDiffApplying(false);
    }
  };

  const {
    data: alignmentResponseData,
    loading: alignmentResponseDataLoading,
    refetch: alignmentResponseRefetch,
  } = useListAlignmentUploadsQuery({
    variables: { DC_ID: dc_id },
    notifyOnNetworkStatusChange: true,
  });
  const [
    deleteAlignmentReport,
    { loading: deleteAlignmentReportLoading },
  ] = useDeleteAlignmentReportByFileIdMutation();

  const {
    data: providerAlignmentResponseData,
    loading: providerAlignmentResponseDataLoading,
    refetch: providerAlignmentRefetch,
  } = useListProviderAlignmentUploadsQuery({
    variables: { DC_ID: dc_id },
    notifyOnNetworkStatusChange: true,
  });
  const [
    deleteProviderAlignmentReport,
    { loading: deleteProviderAlignmentReportLoading },
  ] = useDeleteProviderAlignmentReportByFileIdMutation();

  const {
    data: hedrTemplateResponseData,
    loading: hedrTemplateResponseDataLoading,
    refetch: hedrTemplateResponseRefetch,
  } = useListHedrTemplateUploadsQuery({
    variables: { DC_ID: dc_id },
    notifyOnNetworkStatusChange: true,
    skip: !includeHedrTables,
  });
  const [
    deleteHedrTemplate,
    { loading: deleteHedrTemplateLoading },
  ] = useDeleteHedrTemplateByFileIdMutation();

  const {
    data: healthEquityResponseData,
    loading: healthEquityResponseDataLoading,
    refetch: healthEquityResponseRefetch,
  } = useListHealthEquityReportUploadsQuery({
    variables: { network_number: network_number },
    notifyOnNetworkStatusChange: true,
    skip: !includeHedrTables,
  });
  const [
    deleteHealthEquityReports,
    { loading: deleteHealthEquityReportsLoading },
  ] = useDeleteHealthEquityReportsByFileIdMutation();
  const tableColumnProps = { xs: 24, sm: 24, md: 24, lg: 24, xl: 12 };

  return (
    <div>
      <Row gutter={[4, 40]}>
        <Col {...tableColumnProps}>
          <Table
            title={() => (
              <span>
                <UserInjuredOutlined /> Beneficiary Alignment Reports (ALGC)
                <span style={{ float: 'right' }}>
                  <Space direction="horizontal">
                    <Button
                      type="default"
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => alignmentResponseRefetch()}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        setIsModalVisible(true);
                        setfileCategory('cms_alignment_reports');
                      }}
                      icon={<UploadOutlined />}
                    >
                      Upload
                    </Button>
                  </Space>
                </span>
              </span>
            )}
            loading={alignmentResponseDataLoading}
            bordered
            size="small"
            scroll={{ x: true, y: 120 }}
            pagination={false}
            dataSource={alignmentResponseData?.cms_alignment_reports}
            columns={[
              { title: 'ACO ID', dataIndex: 'dc_id', key: 'dc_id', width: 70 },
              {
                title: 'Performance Year',
                dataIndex: 'performance_year',
                key: 'performance_year',
                width: 160,
                sorter: (a, b) => a.performance_year - b.performance_year,
                defaultSortOrder: 'descend',
              },
              {
                title: 'Updated At',
                dataIndex: 'updated_at',
                key: 'updated_at',
                sorter: (a, b) => a.updated_at.localeCompare(b.updated_at),
                defaultSortOrder: 'descend',
                width: 120,
                render: (text) => (
                  <Tooltip title={new Date(text).toLocaleString()}>
                    <Typography.Text ellipsis style={{ width: 90 }}>
                      {new Date(text).toLocaleDateString()}
                    </Typography.Text>
                  </Tooltip>
                ),
              },
              {
                title: '# Rows',
                dataIndex: ['file_upload', 'num_rows'],
                key: 'num_rows',
                width: 70,
                render: (num) => num?.toLocaleString(),
              },
              {
                title: 'File Name',
                dataIndex: 'file_upload_id',
                key: 'file_upload_id',
                render: (_, record) =>
                  record.file_upload ? (
                    <Typography.Text ellipsis style={{ width: 150 }}>
                      <SignedAWSURL file={record.file_upload} />
                    </Typography.Text>
                  ) : (
                    <Typography.Text
                      ellipsis
                      style={{ width: 150 }}
                      type="secondary"
                    >
                      {record.file_upload_id}
                    </Typography.Text>
                  ),
              },
              {
                title: '',
                key: 'delete',
                width: 50,
                render: (_, record) =>
                  showDeleteActions &&
                  record.file_upload && (
                    <Button
                      type="default"
                      icon={<DeleteOutlined />}
                      loading={deleteAlignmentReportLoading}
                      danger
                      size="small"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete ${record.file_upload?.file_name}?`
                          )
                        ) {
                          deleteAlignmentReport({
                            variables: {
                              dc_id: record.dc_id,
                              file_upload_id: record.file_upload_id!,
                            },
                          })
                            .then((result) => {
                              message.success(
                                `${result.data?.delete_cms_alignment_reports?.affected_rows?.toLocaleString()} rows deleted successfully`
                              );
                              alignmentResponseRefetch();
                            })
                            .catch((e) => {
                              message.error(
                                `Error deleting file: ${e.message}`
                              );
                              console.error(e);
                              alignmentResponseRefetch();
                            });
                        }
                      }}
                    />
                  ),
              },
            ]}
            rowKey={(record) =>
              `${record.dc_id}:${record.performance_year}:${record.file_upload_id}`
            }
          />
        </Col>
        <Col {...tableColumnProps}>
          <Table
            title={() => (
              <span>
                Provider Alignment Reports (PALMR)
                <span style={{ float: 'right' }}>
                  <Space direction="horizontal">
                    <Button
                      type="default"
                      size="small"
                      icon={<ReloadOutlined />}
                      style={{ float: 'right' }}
                      onClick={() => providerAlignmentRefetch()}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        setIsModalVisible(true);
                        setfileCategory('cms_provider_alignment_reports');
                      }}
                      icon={<UploadOutlined />}
                    >
                      Upload
                    </Button>
                  </Space>
                </span>
              </span>
            )}
            loading={providerAlignmentResponseDataLoading}
            bordered
            size="small"
            scroll={{ x: true, y: 120 }}
            pagination={false}
            dataSource={
              providerAlignmentResponseData?.cms_provider_alignment_reports
            }
            columns={[
              {
                title: 'ACO ID',
                dataIndex: 'ACO_ID',
                key: 'ACO_ID',
                width: 70,
              },
              {
                title: 'Performance Year',
                dataIndex: 'PERFORMANCE_YEAR',
                key: 'PERFORMANCE_YEAR',
                width: 160,
                sorter: (a, b) => a.PERFORMANCE_YEAR - b.PERFORMANCE_YEAR,
                defaultSortOrder: 'descend',
              },
              {
                title: 'Updated At',
                dataIndex: 'updated_at',
                key: 'updated_at',
                sorter: (a, b) => a.updated_at.localeCompare(b.updated_at),
                defaultSortOrder: 'descend',
                width: 120,
                render: (text) => (
                  <Tooltip title={new Date(text).toLocaleString()}>
                    <Typography.Text ellipsis style={{ width: 90 }}>
                      {new Date(text).toLocaleDateString()}
                    </Typography.Text>
                  </Tooltip>
                ),
              },
              {
                title: '# Rows',
                dataIndex: ['file_upload', 'num_rows'],
                key: 'num_rows',
                width: 70,
                render: (num) => num?.toLocaleString(),
              },
              {
                title: 'File Name',
                dataIndex: 'file_upload_id',
                key: 'file_upload_id',
                render: (_, record) =>
                  record.file_upload ? (
                    <Typography.Text ellipsis style={{ width: 150 }}>
                      <SignedAWSURL file={record.file_upload} />
                    </Typography.Text>
                  ) : (
                    <Typography.Text
                      ellipsis
                      style={{ width: 150 }}
                      type="secondary"
                    >
                      {record.file_upload_id}
                    </Typography.Text>
                  ),
              },
              {
                title: '',
                key: 'delete',
                width: 50,
                render: (_, record) =>
                  showDeleteActions &&
                  record.file_upload && (
                    <Button
                      type="default"
                      icon={<DeleteOutlined />}
                      loading={deleteProviderAlignmentReportLoading}
                      danger
                      size="small"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete ${record.file_upload?.file_name}?`
                          )
                        ) {
                          deleteProviderAlignmentReport({
                            variables: {
                              aco_id: record.ACO_ID,
                              file_upload_id: record.file_upload_id!,
                            },
                          })
                            .then((result) => {
                              message.success(
                                `${result.data?.delete_cms_provider_alignment_reports?.affected_rows?.toLocaleString()} rows deleted successfully`
                              );
                              providerAlignmentRefetch();
                            })
                            .catch((e) => {
                              message.error(
                                `Error deleting file: ${e.message}`
                              );
                              console.error(e);
                              providerAlignmentRefetch();
                            });
                        }
                      }}
                    />
                  ),
              },
            ]}
            rowKey={(record) =>
              `${record.ACO_ID}:${record.PERFORMANCE_YEAR}:${record.file_upload_id}`
            }
          />
        </Col>
        <Col {...tableColumnProps}>
          <Table
            title={() => (
              <span>
                <FileSyncOutlined /> SVA Response Files (PBVAR)
                <span style={{ float: 'right' }}>
                  <Space direction="horizontal">
                    <Button
                      type="default"
                      size="small"
                      icon={<ReloadOutlined />}
                      style={{ float: 'right' }}
                      onClick={() => pvaResponseRefetch()}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        setIsModalVisible(true);
                        setfileCategory('cms_pva_responses');
                      }}
                      icon={<UploadOutlined />}
                    >
                      Upload
                    </Button>
                  </Space>
                </span>
              </span>
            )}
            loading={pvaResponseDataLoading}
            bordered
            size="small"
            scroll={{ x: true, y: 120 }}
            pagination={false}
            dataSource={pvaResponseData?.cms_pva_responses}
            columns={[
              {
                title: 'ACO ID',
                dataIndex: 'DCE_ID',
                key: 'DCE_ID',
                width: 70,
              },
              {
                title: 'Submission Date',
                dataIndex: 'SUBMISSION_DATE',
                key: 'SUBMISSION_DATE',
                width: 160,
                sorter: (a, b) =>
                  a.SUBMISSION_DATE.localeCompare(b.SUBMISSION_DATE),
                defaultSortOrder: 'descend',
              },
              {
                title: 'Created At',
                dataIndex: 'created_at',
                key: 'created_at',
                sorter: (a, b) => a.created_at.localeCompare(b.created_at),
                defaultSortOrder: 'descend',
                width: 120,
                render: (text) => (
                  <Tooltip title={new Date(text).toLocaleString()}>
                    <Typography.Text ellipsis style={{ width: 90 }}>
                      {new Date(text).toLocaleDateString()}
                    </Typography.Text>
                  </Tooltip>
                ),
              },
              {
                title: '# Rows',
                dataIndex: ['file_upload', 'num_rows'],
                key: 'num_rows',
                width: 70,
                render: (num) => num?.toLocaleString(),
              },
              {
                title: 'File Name',
                dataIndex: 'file_upload_id',
                key: 'file_upload_id',
                render: (_, record) =>
                  record.file_upload ? (
                    <Typography.Text ellipsis style={{ width: 150 }}>
                      <SignedAWSURL file={record.file_upload} />
                    </Typography.Text>
                  ) : (
                    <Typography.Text
                      ellipsis
                      style={{ width: 150 }}
                      type="secondary"
                    >
                      {record.file_upload_id}
                    </Typography.Text>
                  ),
              },
              {
                title: '',
                key: 'delete',
                width: 50,
                render: (_, record) =>
                  showDeleteActions &&
                  record.file_upload && (
                    <Button
                      type="default"
                      icon={<DeleteOutlined />}
                      loading={deletePVAResponseDataLoading}
                      danger
                      size="small"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete ${record.file_upload?.file_name}?`
                          )
                        ) {
                          deletePvaResponse({
                            variables: {
                              DC_ID: record.DCE_ID,
                              file_upload_id: record.file_upload_id!,
                            },
                          })
                            .then((result) => {
                              message.success(
                                `${result.data?.delete_cms_pva_responses?.affected_rows?.toLocaleString()} rows deleted successfully`
                              );
                              pvaResponseRefetch();
                            })
                            .catch((e) => {
                              message.error(
                                `Error deleting file: ${e.message}`
                              );
                              console.error(e);
                              pvaResponseRefetch();
                            });
                        }
                      }}
                    />
                  ),
              },
            ]}
            rowKey={(record) =>
              `${record.DCE_ID}:${record.SUBMISSION_DATE}:${record.file_upload_id}`
            }
          />
        </Col>
        <Col {...tableColumnProps}>
          <Table
            title={() => (
              <span>
                <StethoscopeOutlined /> Participant Lists
                <span style={{ float: 'right' }}>
                  <Space direction="horizontal">
                    <Button
                      type="default"
                      size="small"
                      icon={<ReloadOutlined />}
                      style={{ float: 'right' }}
                      onClick={() => participantResponseRefetch()}
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => {
                        setIsModalVisible(true);
                        setfileCategory('cms_dce_participants');
                      }}
                      icon={<UploadOutlined />}
                    >
                      Upload
                    </Button>
                  </Space>
                </span>
              </span>
            )}
            loading={participantResponseDataLoading}
            bordered
            size="small"
            scroll={{ x: true, y: 120 }}
            pagination={false}
            dataSource={participantResponseData?.cms_dce_participants}
            columns={[
              {
                title: 'ACO ID',
                dataIndex: 'entity_id',
                key: 'entity_id',
                width: 70,
              },
              {
                title: 'Performance Year',
                dataIndex: 'performance_year',
                key: 'performance_year',
                width: 160,
                sorter: (a, b) =>
                  (a.performance_year || '').localeCompare(
                    b.performance_year || ''
                  ),
                defaultSortOrder: 'descend',
              },
              {
                title: 'Created At',
                dataIndex: 'created_at',
                key: 'created_at',
                sorter: (a, b) => a.created_at.localeCompare(b.created_at),
                defaultSortOrder: 'descend',
                width: 120,
                render: (text) => (
                  <Tooltip title={new Date(text).toLocaleString()}>
                    <Typography.Text ellipsis style={{ width: 90 }}>
                      {new Date(text).toLocaleDateString()}
                    </Typography.Text>
                  </Tooltip>
                ),
              },
              {
                title: '# Rows',
                dataIndex: ['file_upload', 'num_rows'],
                key: 'num_rows',
                width: 70,
                render: (num) => num?.toLocaleString(),
              },
              {
                title: 'File Name',
                dataIndex: 'file_upload_id',
                key: 'file_upload_id',
                render: (_, record) =>
                  record.file_upload ? (
                    <Typography.Text ellipsis style={{ width: 150 }}>
                      <SignedAWSURL file={record.file_upload} />
                    </Typography.Text>
                  ) : (
                    <Typography.Text
                      ellipsis
                      style={{ width: 150 }}
                      type="secondary"
                    >
                      {record.file_upload_id}
                    </Typography.Text>
                  ),
              },
              {
                title: '',
                key: 'delete',
                width: showDeleteActions ? 50 : 0,
                render: (_, record) =>
                  showDeleteActions &&
                  record.file_upload && (
                    <Button
                      type="default"
                      icon={<DeleteOutlined />}
                      loading={deleteParticipantListLoading}
                      danger
                      size="small"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Are you sure you want to delete ${record.file_upload?.file_name}?`
                          )
                        ) {
                          deleteParticipantList({
                            variables: {
                              entity_id: record.entity_id!,
                              file_upload_id: record.file_upload_id!,
                            },
                          })
                            .then((result) => {
                              message.success(
                                `${result.data?.delete_cms_dce_participants?.affected_rows?.toLocaleString()} rows deleted successfully`
                              );
                              participantResponseRefetch();
                            })
                            .catch((e) => {
                              message.error(
                                `Error deleting file: ${e.message}`
                              );
                              console.error(e);
                              participantResponseRefetch();
                            });
                        }
                      }}
                    />
                  ),
              },
              {
                title: 'Action',
                key: 'participant_diff',
                width: 150,
                render: (_: unknown, record: ParticipantUploadRow) =>
                  record.file_upload && (
                    <Tooltip
                      title={
                        record.file_upload.upload_status === 'Completed'
                          ? 'Create and/or archive practices to match the TIN/CCNs in this list'
                          : 'The upload must finish before changes can be reviewed'
                      }
                    >
                      <Button
                        type="primary"
                        icon={<DiffOutlined />}
                        loading={
                          participantDiffLoadingId === record.file_upload_id
                        }
                        disabled={
                          record.file_upload.upload_status !== 'Completed'
                        }
                        size="small"
                        onClick={() =>
                          previewParticipantDiff(
                            record.file_upload_id!,
                            record.file_upload!.file_name
                          )
                        }
                      >
                        Sync Practices
                      </Button>
                    </Tooltip>
                  ),
              },
            ]}
            rowKey={(record) =>
              `${record.entity_id}:${record.performance_year}:${record.file_upload_id}`
            }
          />
        </Col>
        {includeHedrTables && (
          <Col {...tableColumnProps}>
            <Table
              title={() => (
                <span>
                  <FileOutlined /> HEDR Submission Templates
                  <span style={{ float: 'right' }}>
                    <Space direction="horizontal">
                      <Button
                        type="default"
                        size="small"
                        icon={<ReloadOutlined />}
                        style={{ float: 'right' }}
                        onClick={() => hedrTemplateResponseRefetch()}
                      />
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          setIsModalVisible(true);
                          setfileCategory('hedr_submission_templates');
                        }}
                        icon={<UploadOutlined />}
                      >
                        Upload
                      </Button>
                    </Space>
                  </span>
                </span>
              )}
              loading={hedrTemplateResponseDataLoading}
              bordered
              size="small"
              scroll={{ x: true, y: 120 }}
              pagination={false}
              dataSource={hedrTemplateResponseData?.hedr_submission_templates}
              columns={[
                {
                  title: 'ACO ID',
                  dataIndex: 'entity_id',
                  key: 'entity_id',
                  width: 70,
                },
                {
                  title: 'Performance Year',
                  dataIndex: 'performance_year',
                  key: 'performance_year',
                  width: 160,
                  sorter: (a, b) => a.performance_year - b.performance_year,
                  defaultSortOrder: 'descend',
                },
                {
                  title: 'Created At',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  sorter: (a, b) => a.created_at.localeCompare(b.created_at),
                  defaultSortOrder: 'descend',
                  width: 120,
                  render: (text) => (
                    <Tooltip title={new Date(text).toLocaleString()}>
                      <Typography.Text ellipsis style={{ width: 90 }}>
                        {new Date(text).toLocaleDateString()}
                      </Typography.Text>
                    </Tooltip>
                  ),
                },
                {
                  title: '# Rows',
                  dataIndex: ['file_upload', 'num_rows'],
                  key: 'num_rows',
                  width: 70,
                  render: (num) => num?.toLocaleString(),
                },
                {
                  title: 'File Name',
                  dataIndex: 'file_upload_id',
                  key: 'file_upload_id',
                  render: (_, record) =>
                    record.file_upload ? (
                      <Typography.Text ellipsis style={{ width: 150 }}>
                        <SignedAWSURL file={record.file_upload} />
                      </Typography.Text>
                    ) : (
                      <Typography.Text
                        ellipsis
                        style={{ width: 150 }}
                        type="secondary"
                      >
                        {record.file_upload_id}
                      </Typography.Text>
                    ),
                },
                {
                  title: '',
                  key: 'delete',
                  width: 50,
                  render: (_, record) =>
                    showDeleteActions &&
                    record.file_upload && (
                      <Button
                        type="default"
                        icon={<DeleteOutlined />}
                        loading={deleteHedrTemplateLoading}
                        danger
                        size="small"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete ${record.file_upload?.file_name}?`
                            )
                          ) {
                            deleteHedrTemplate({
                              variables: {
                                DC_ID: record.entity_id,
                                file_upload_id: record.file_upload_id!,
                              },
                            })
                              .then((result) => {
                                message.success(
                                  `${result.data?.delete_hedr_submission_templates?.affected_rows?.toLocaleString()} rows deleted successfully`
                                );
                                hedrTemplateResponseRefetch();
                              })
                              .catch((e) => {
                                message.error(
                                  `Error deleting file: ${e.message}`
                                );
                                console.error(e);
                                hedrTemplateResponseRefetch();
                              });
                          }
                        }}
                      />
                    ),
                },
              ]}
              rowKey={(record) =>
                `${record.entity_id}:${record.performance_year}:${record.file_upload_id}`
              }
            />
          </Col>
        )}
        {includeHedrTables && (
          <Col {...tableColumnProps}>
            <Table
              title={() => (
                <span>
                  <FileOutlined /> Health Equity Report Files
                  <span style={{ float: 'right' }}>
                    <Space direction="horizontal">
                      <Button
                        type="default"
                        size="small"
                        icon={<ReloadOutlined />}
                        style={{ float: 'right' }}
                        onClick={() => healthEquityResponseRefetch()}
                      />
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          setIsModalVisible(true);
                          setfileCategory('health_equity_reports');
                        }}
                        icon={<UploadOutlined />}
                      >
                        Upload
                      </Button>
                    </Space>
                  </span>
                </span>
              )}
              loading={healthEquityResponseDataLoading}
              bordered
              size="small"
              scroll={{ x: true, y: 120 }}
              pagination={false}
              dataSource={healthEquityResponseData?.health_equity_reports}
              columns={[
                {
                  title: 'Network #',
                  dataIndex: 'network_number',
                  key: 'network_number',
                  width: 90,
                },
                {
                  title: 'Created At',
                  dataIndex: ['file_upload', 'created_at'],
                  key: 'created_at',
                  sorter: (a, b) =>
                    a.file_upload?.created_at.localeCompare(
                      b.file_upload?.created_at
                    ),
                  defaultSortOrder: 'descend',
                  width: 120,
                  render: (text) => (
                    <Tooltip title={new Date(text).toLocaleString()}>
                      <Typography.Text ellipsis style={{ width: 90 }}>
                        {new Date(text).toLocaleDateString()}
                      </Typography.Text>
                    </Tooltip>
                  ),
                },
                {
                  title: 'Template Version',
                  dataIndex: 'template_version',
                  key: 'template_version',
                  width: 160,
                  sorter: (a, b) =>
                    (a.template_version || '').localeCompare(
                      b.template_version || ''
                    ),
                },
                {
                  title: '# Rows',
                  dataIndex: ['file_upload', 'num_rows'],
                  key: 'num_rows',
                  width: 70,
                  render: (num) => num?.toLocaleString(),
                },
                {
                  title: 'File Name',
                  dataIndex: 'file_upload_id',
                  key: 'file_upload_id',
                  render: (_, record) =>
                    record.file_upload ? (
                      <Typography.Text ellipsis style={{ width: 150 }}>
                        <SignedAWSURL file={record.file_upload} />
                      </Typography.Text>
                    ) : (
                      <Typography.Text
                        ellipsis
                        style={{ width: 150 }}
                        type="secondary"
                      >
                        {record.file_upload_id}
                      </Typography.Text>
                    ),
                },
                {
                  title: '',
                  key: 'delete',
                  width: 50,
                  render: (_, record) =>
                    showDeleteActions &&
                    record.file_upload && (
                      <Button
                        type="default"
                        icon={<DeleteOutlined />}
                        loading={deleteHealthEquityReportsLoading}
                        danger
                        size="small"
                        onClick={() => {
                          if (
                            window.confirm(
                              `Are you sure you want to delete ${record.file_upload?.file_name}?`
                            )
                          ) {
                            deleteHealthEquityReports({
                              variables: {
                                network_number: record.network_number!,
                                file_upload_id: record.file_upload_id!,
                              },
                            })
                              .then((result) => {
                                message.success(
                                  `${result.data?.delete_health_equity_reports?.affected_rows?.toLocaleString()} rows deleted successfully`
                                );
                                healthEquityResponseRefetch();
                              })
                              .catch((e) => {
                                message.error(
                                  `Error deleting file: ${e.message}`
                                );
                                console.error(e);
                                healthEquityResponseRefetch();
                              });
                          }
                        }}
                      />
                    ),
                },
              ]}
              rowKey={(record) =>
                `${record.network_number}:${record.file_upload_id}`
              }
            />
          </Col>
        )}
      </Row>
      <CMSUploadModal
        network_id={network_id}
        network_number={network_number}
        dc_id={dc_id}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onUpload={async (file, fileUploadId) => {
          setIsModalVisible(false);
          message.success(`${file.name} uploaded successfully`);
          if (fileCategory === 'cms_alignment_reports') {
            alignmentResponseRefetch();
          } else if (fileCategory === 'cms_provider_alignment_reports') {
            providerAlignmentRefetch();
          } else if (fileCategory === 'cms_dce_participants') {
            participantResponseRefetch();
            await previewParticipantDiff(fileUploadId, file.name);
          } else if (fileCategory === 'cms_pva_responses') {
            pvaResponseRefetch();
          } else if (fileCategory === 'hedr_submission_templates') {
            hedrTemplateResponseRefetch();
          } else if (fileCategory === 'health_equity_reports') {
            healthEquityResponseRefetch();
          }
        }}
        fileCategory={fileCategory}
      />
      <Modal
        visible={Boolean(participantDiffPreview)}
        width={900}
        title={
          'Review participant list changes' +
          (participantDiffPreview
            ? ' — ' + participantDiffPreview.file_name
            : '')
        }
        okText="Confirm changes"
        cancelText="Cancel"
        confirmLoading={participantDiffApplying}
        okButtonProps={{
          disabled:
            Boolean(
              participantDiffPreview?.archive_conflicts.length ||
                participantDiffPreview?.reinstate_conflicts.length
            ) ||
            (participantDiffPreview?.archives.length === 0 &&
              participantDiffPreview?.reinstates.length === 0 &&
              participantDiffPreview?.creates.length === 0),
        }}
        onCancel={() => {
          if (!participantDiffApplying) setParticipantDiffPreview(undefined);
        }}
        onOk={applyParticipantDiff}
      >
        {participantDiffPreview?.archive_conflicts.length ||
        participantDiffPreview?.reinstate_conflicts.length ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Some practices already have open change requests"
            description="Resolve those requests before confirming this participant list diff. No changes have been made."
          />
        ) : null}
        {participantDiffPreview?.skipped_no_tin ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              participantDiffPreview.skipped_no_tin.toLocaleString() +
              ' active practices without a TIN could not be compared'
            }
          />
        ) : null}
        {participantDiffPreview &&
        participantDiffPreview.archives.length === 0 &&
        participantDiffPreview.reinstates.length === 0 &&
        participantDiffPreview.creates.length === 0 &&
        participantDiffPreview.archive_conflicts.length === 0 &&
        participantDiffPreview.reinstate_conflicts.length === 0 ? (
          <Alert
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
            message="This participant list does not propose any changes"
          />
        ) : null}

        {participantDiffPreview?.creates.length ? (
          <>
            <Typography.Title level={4}>Practices to create</Typography.Title>
            <Table
              size="small"
              bordered
              pagination={false}
              rowKey={(record) =>
                [record.primary_network_id, record.tin, record.ccn || ''].join(
                  ':'
                )
              }
              dataSource={participantDiffPreview.creates}
              columns={[
                {
                  title: '',
                  key: 'change',
                  align: 'center',
                  width: 44,
                  render: () => (
                    <Tooltip title="Create practice">
                      <PlusCircleFilled
                        aria-label="Create practice"
                        style={{ color: '#52c41a', fontSize: 18 }}
                      />
                    </Tooltip>
                  ),
                },
                { title: 'Practice', dataIndex: 'practice_name' },
                { title: 'TIN', dataIndex: 'tin', width: 120 },
                { title: 'CCN', dataIndex: 'ccn', width: 110 },
                { title: 'Network', dataIndex: 'network_name', width: 200 },
              ]}
            />
          </>
        ) : null}

        {participantDiffPreview &&
        (participantDiffPreview.reinstates.length ||
          participantDiffPreview.reinstate_conflicts.length) ? (
          <>
            <Typography.Title level={4} style={{ marginTop: 24 }}>
              Practices to unarchive
            </Typography.Title>
            <Table
              size="small"
              bordered
              pagination={false}
              rowKey={(record) => record.practice_id!}
              dataSource={[
                ...participantDiffPreview.reinstates,
                ...participantDiffPreview.reinstate_conflicts,
              ]}
              columns={[
                {
                  title: '',
                  key: 'change',
                  align: 'center',
                  width: 44,
                  render: () => (
                    <Tooltip title="Unarchive practice">
                      <PlusCircleFilled
                        aria-label="Unarchive practice"
                        style={{ color: '#52c41a', fontSize: 18 }}
                      />
                    </Tooltip>
                  ),
                },
                { title: 'Practice', dataIndex: 'practice_name' },
                { title: 'TIN', dataIndex: 'tin', width: 120 },
                { title: 'CCN', dataIndex: 'ccn', width: 110 },
                { title: 'Network', dataIndex: 'network_name', width: 200 },
              ]}
            />
          </>
        ) : null}

        {participantDiffPreview &&
        (participantDiffPreview.archives.length ||
          participantDiffPreview.archive_conflicts.length) ? (
          <>
            <Typography.Title level={4} style={{ marginTop: 24 }}>
              Practices to archive
            </Typography.Title>
            <Table
              size="small"
              bordered
              pagination={false}
              rowKey={(record) => record.practice_id!}
              dataSource={[
                ...participantDiffPreview.archives,
                ...participantDiffPreview.archive_conflicts,
              ]}
              columns={[
                {
                  title: '',
                  key: 'change',
                  align: 'center',
                  width: 44,
                  render: () => (
                    <Tooltip title="Archive practice">
                      <MinusCircleFilled
                        aria-label="Archive practice"
                        style={{ color: '#ff4d4f', fontSize: 18 }}
                      />
                    </Tooltip>
                  ),
                },
                { title: 'Practice', dataIndex: 'practice_name' },
                { title: 'TIN', dataIndex: 'tin', width: 120 },
                { title: 'CCN', dataIndex: 'ccn', width: 110 },
                { title: 'Network', dataIndex: 'network_name', width: 200 },
              ]}
            />
          </>
        ) : null}
      </Modal>
    </div>
  );
};

export default NetworkDescription;
